import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { TouchBackend } from 'react-dnd-touch-backend'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import withScrolling from 'react-dnd-scrolling'
import useIdentities from '../hooks/api/useIdentities'
import usePipelines from '../hooks/api/usePipelines'
import useProducts from '../hooks/api/useProducts'
import Stage from './Stage'
import useIsMobile from '../hooks/useIsMobile'
import OtherPipelineDropArea from './OtherPipelineDropArea'
import Filters from '../common/Filters'
import useIsAdmin from '../hooks/useIsAdmin'
import useCurrentIdentity from '../hooks/useCurrentIdentity'
import useCards from '../hooks/api/useCards'
import PageTitle from '../common/PageTitle'
import OpportunityEditModal from './OpportunityEditModal'
import { usePreview } from 'react-dnd-preview'
import { grey } from '@mui/material/colors'
import useOtherAvatar from '../hooks/api/useOtherAvatar'
import { debounce } from '../utils'

const DragPreview = () => {
  const preview = usePreview()
  if (!preview.display) {
    return null
  }
  const { style } = preview

  return (
    <Box
      sx={{
        width: '300px',
        height: '100px',
        backgroundColor: grey[400],
        borderRadius: '4px',
        zIndex: 100,
        ...style,
      }}
    ></Box>
  )
}

const ScrollingComponent = withScrolling('div')

const PipelineView = () => {
  const isMobile = useIsMobile()
  const { isPriveledged } = useIsAdmin()

  const { currentIdentity } = useCurrentIdentity()

  const {
    pipelines,
    selectedPipeline,
    selectedPipelineId,
    setSelectedPipelineId,
    selectPipeline,
    ..._pipelines
  } = usePipelines()

  const [selectedIdentities, setSelectedIdentities] = useState([])
  const [selectedCustomers, setSelectedCustomers] = useState([])
  const [selectedStatuses, setSelectedStatuses] = useState([
    { id: 1, name: 'Open' },
  ])

  const [filtersOpen, setFiltersOpen] = useState(false)

  const [editingOpportunity, setEditingOpportunity] = useState(null)

  const filters = {
    identity_ids: isPriveledged
      ? selectedIdentities.map((i) => i.id)
      : [currentIdentity.id],
    // identity_ids: selectedIdentities.map((i) => i.id), // ASK: should users be able to see other user's cards in the cardwall
    customer_ids: selectedCustomers.map((i) => i.id),
    status_ids: selectedStatuses.map((s) => s.id),
  }

  const { cards, moveCard, updateCard } = useCards(selectedPipelineId, filters)

  const { products, billingCycleTypes, ..._products } = useProducts(
    null,
    false,
    true
  )
  const { identities, ..._identities } = useIdentities()

  const isLoading =
    _pipelines.isLoading || _products.isLoading || _identities.isLoading

  const avatarsNeeded = [
    ...new Set(
      cards
        .map(
          (c) =>
            (
              (c.accountOwners || []).find((ao) => ao.isDefault) ||
              c.accountOwners?.[0]
            )?.id
        )
        .filter((id) => !!id)
    ),
  ]
  const { avatarUrlsMap } = useOtherAvatar(avatarsNeeded)

  const setScroll = useCallback(
    debounce((e) => {
      localStorage.setItem(
        'pipeline-view-scroll-offset',
        `${e.target.scrollLeft}`
      )
    }, 100),
    []
  )

  const scrollContainer = document.getElementById('pipeline-scroll-container')

  useEffect(() => {
    const scrollOffset = localStorage.getItem('pipeline-view-scroll-offset')

    if (scrollOffset && scrollContainer) {
      scrollContainer.scrollLeft = Number(scrollOffset)
    }
  }, [scrollContainer])

  if (isLoading) return <CircularProgress />

  return (
    <>
      <OpportunityEditModal
        editingOpportunity={editingOpportunity}
        onClose={() => setEditingOpportunity(null)}
        onSubmit={(newVal) => {
          updateCard(newVal)
          setEditingOpportunity(null)
        }}
        pipelines={pipelines}
        billingCycleTypes={billingCycleTypes}
        products={products}
      ></OpportunityEditModal>
      <PageTitle>Pipeline/Cardwall</PageTitle>
      <DndProvider
        backend={isMobile ? TouchBackend : HTML5Backend}
        options={{ delay: isMobile ? 1000 : null }}
      >
        {isMobile && <DragPreview />}
        <Box
          sx={{
            mt: 2,
            height: 'calc(100% - 48px)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ mx: 1, display: 'flex', mb: 2 }}>
            {selectedPipeline && (
              <TextField
                margin='none'
                label={'Select a pipeline'}
                value={selectedPipelineId}
                onChange={selectPipeline}
                select
                sx={{ width: '50%' }}
              >
                {pipelines.map((p, i) => (
                  <MenuItem key={i} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <Button onClick={() => setFiltersOpen((prev) => !prev)}>
              {filtersOpen ? 'Close Filters' : 'Show Filters'}
            </Button>
          </Box>
          <Box sx={{ ml: 1, mb: 2 }}>
            {filtersOpen && (
              <Filters
                filters={[
                  {
                    key: 'identity',
                    props: { selectedIdentities, setSelectedIdentities },
                    disabled: !isPriveledged,
                  },
                  {
                    key: 'customer',
                    props: { selectedCustomers, setSelectedCustomers },
                  },
                  {
                    key: 'status',
                    props: { selectedStatuses, setSelectedStatuses },
                  },
                ]}
              />
            )}
          </Box>

          <ScrollingComponent
            id='pipeline-scroll-container'
            style={{ display: 'flex', flexGrow: 1, overflow: 'scroll' }}
            onScroll={setScroll}
          >
            {selectedPipeline.stages.map((s) => {
              const stageCards = cards.filter(
                (c) => c.stageId === s.id || c.stageId === s.id
              )

              return (
                <Stage
                  avatarUrlsMap={avatarUrlsMap}
                  cards={stageCards}
                  moveCard={moveCard}
                  stage={s}
                  key={s.id}
                  products={products}
                  billingCycleTypes={billingCycleTypes}
                  pipelines={pipelines}
                  identities={identities}
                  selectedPipeline={selectedPipeline}
                  setEditingOpportunity={setEditingOpportunity}
                />
              )
            })}
          </ScrollingComponent>
        </Box>
      </DndProvider>
    </>
  )
}

export default PipelineView
